/**
 * mSTEM Tutorial Modal container (PRJ-06 + PRJ-07 / LLD UC 09-10).
 *
 * Owns:
 *   - Document-level keyboard listener for ArrowDown/PageDown (next),
 *     ArrowUp/PageUp (prev), Escape (close). Guarded against firing
 *     while an INPUT or TEXTAREA element is focused so future text
 *     widgets in tutorial content (search, notes) don't fight the
 *     navigation keys.
 *   - scrollIntoView on active chapter change so the sidebar item stays
 *     visible during keyboard navigation through long chapter lists.
 *   - Default-active-chapter on mount: if reducer state hasn't selected
 *     a chapter yet, dispatch setActiveChapter(firstChapter.id) so the
 *     content panel always renders something on first open.
 *
 * Reads:
 *   - state.scratchGui.tutorialModal.type ('ide' | 'firmware' | null)
 *   - state.scratchGui.tutorialModal.activeChapterId
 *
 * Chapter sources:
 *   - IDE_CHAPTERS from Wave 3a (Plan 03-07a) — Wave 3b populates sections.
 *   - FIRMWARE_CHAPTERS from Wave 0 stub (Plan 03-01) — Wave 4 (Plan 03-08)
 *     populates content. Firmware is acceptable as empty until then per
 *     warning W-11; the component renders the gen.tutorial.contentError
 *     panel gracefully.
 *
 * W-9 fix: intl propType omitted — this container body never reads
 * this.props.intl directly. The inner presentational component is
 * separately wrapped via injectIntl.
 */
import bindAll from 'lodash.bindall';
import PropTypes from 'prop-types';
import React from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';

import {closeTutorialModal} from '../reducers/modals';
import {setActiveChapter, closeTutorial} from '../reducers/tutorial-modal';
import IDE_CHAPTERS from '../lib/mstem-mock/tutorials/ide-chapters';
import FIRMWARE_CHAPTERS from '../lib/mstem-mock/tutorials/firmware-chapters';

import MstemTutorialModalComponent from '../components/mstem-tutorial-modal/mstem-tutorial-modal.jsx';

class MstemTutorialModal extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleSetActive',
            'handleNext',
            'handlePrev',
            'handleClose',
            'handleKeyDown'
        ]);
    }
    componentDidMount () {
        if (typeof document !== 'undefined') {
            document.addEventListener('keydown', this.handleKeyDown);
        }
        const chapters = this.getChapters();
        if (!this.props.activeChapterId && chapters.length > 0) {
            this.props.onSetActiveChapter(chapters[0].id);
        }
    }
    componentDidUpdate (prevProps) {
        if (prevProps.activeChapterId !== this.props.activeChapterId) {
            if (typeof document !== 'undefined') {
                const el = document.querySelector(
                    `[data-chapter-id="${this.props.activeChapterId}"]`
                );
                if (el && el.scrollIntoView) {
                    el.scrollIntoView({block: 'nearest', behavior: 'smooth'});
                }
            }
        }
    }
    componentWillUnmount () {
        if (typeof document !== 'undefined') {
            document.removeEventListener('keydown', this.handleKeyDown);
        }
    }
    getChapters () {
        return this.props.type === 'firmware' ? FIRMWARE_CHAPTERS : IDE_CHAPTERS;
    }
    handleKeyDown (e) {
        const active = typeof document === 'undefined' ? null : document.activeElement;
        const tag = active && active.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA') return;

        if (e.key === 'ArrowDown' || e.key === 'PageDown') {
            e.preventDefault();
            this.handleNext();
        } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
            e.preventDefault();
            this.handlePrev();
        } else if (e.key === 'Escape') {
            this.handleClose();
        }
    }
    handleSetActive (id) {
        this.props.onSetActiveChapter(id);
    }
    handleNext () {
        const chapters = this.getChapters();
        const idx = chapters.findIndex(c => c.id === this.props.activeChapterId);
        if (idx >= 0 && idx < chapters.length - 1) {
            this.props.onSetActiveChapter(chapters[idx + 1].id);
        }
    }
    handlePrev () {
        const chapters = this.getChapters();
        const idx = chapters.findIndex(c => c.id === this.props.activeChapterId);
        if (idx > 0) {
            this.props.onSetActiveChapter(chapters[idx - 1].id);
        }
    }
    handleClose () {
        this.props.onClose();
    }
    render () {
        const chapters = this.getChapters();
        return (
            <MstemTutorialModalComponent
                activeChapterId={this.props.activeChapterId}
                chapters={chapters}
                type={this.props.type || 'ide'}
                onClose={this.handleClose}
                onNext={this.handleNext}
                onPrev={this.handlePrev}
                onSetActive={this.handleSetActive}
            />
        );
    }
}

MstemTutorialModal.propTypes = {
    // W-9 FIX: NO intlShape — this container body never reads this.props.intl.
    activeChapterId: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    onSetActiveChapter: PropTypes.func.isRequired,
    type: PropTypes.oneOf(['ide', 'firmware'])
};

MstemTutorialModal.defaultProps = {
    activeChapterId: null,
    type: 'ide'
};

const mapStateToProps = state => ({
    activeChapterId: state.scratchGui.tutorialModal.activeChapterId,
    type: state.scratchGui.tutorialModal.type
});

const mapDispatchToProps = dispatch => ({
    onClose: () => {
        dispatch(closeTutorialModal());
        dispatch(closeTutorial());
    },
    onSetActiveChapter: id => dispatch(setActiveChapter(id))
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps)
)(MstemTutorialModal);
